import { fetchUserAttributes, getCurrentUser } from "../utils/session";
import { Avatar } from "baseui/avatar";
import { Button } from "baseui/button";
import {
  ALIGN,
  HeaderNavigation,
  StyledNavigationItem as NavigationItem,
  StyledNavigationList as NavigationList,
} from "baseui/header-navigation";
import { StatefulMenu } from "baseui/menu";
import { StatefulPopover, StatefulPopover as Popover } from "baseui/popover";
import { useEffect, useState } from "react";
import { Bell, ChevronDown } from "lucide-react";
import { Flex } from "./Flex";
import { useBusinessContext } from "../context/BusinessContext";
import { styled } from "styletron-react";
import { useT, type Lang } from "../i18n/admin";
import { zIndex } from "../styles/zIndex";
import { initialsOf } from "./phone/PhoneCards";
import { useHeaderTitle } from "../hooks/useHeaderTitle";

// Above sticky page toolbars (the Dashboard's is z-index 100), or they cover the menu.
const POPOVER_OVERRIDES = { Body: { style: { zIndex: zIndex.dropdown } } };

type Props = {
  isAuthenticated: boolean;
  onSignIn?: () => void;
  onLogOut?: () => void;
  onMenuClick?: () => void;
};

const BusinessName = styled('div', {
  fontSize: '22px',
  fontWeight: '700',
  color: '#000',
  float: 'right',
});

const BusinessSelector = styled(Button, {
  fontSize: '16px',
  fontWeight: '500',
  padding: '8px 16px',
  marginRight: '20px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

// SAM-683: the language selector lives in the user menu. Each language is named in itself.
const LANGUAGE_NAMES: Record<Lang, string> = { en: "English", es: "Español" }; // i18n-ignore

export const AppHeader = ({ onSignIn, onLogOut, isAuthenticated, onMenuClick }: Props) => {
  const [user, setUser] = useState<any>(null);
  const { t, lang, setLang } = useT();
  const menuItems = [
    ...(Object.keys(LANGUAGE_NAMES) as Lang[]).map((l) => ({
      id: `lang:${l}`,
      label: `${l === lang ? "✓ " : "\u2003"}${LANGUAGE_NAMES[l]}`,
    })),
    { id: "logout", label: t("header.logOut") },
  ];
  const { 
    business, 
    availableBusinesses, 
    switchBusiness,
    currentBusinessId
  } = useBusinessContext();

  const hasMultipleBusinesses = availableBusinesses.length > 1;
  // SAM-1309 (U11): the business, else the estate group or the tenant; «Business Admin» only while nothing is known.
  const headerTitle = useHeaderTitle(t("header.appName"));
  

  // Función para obtener URL del logo (del negocio o por defecto)
  const getLogoUrl = (): string => {
    if (business?.logo_url) {
      return business.logo_url;
    }
    
    // Intentar leer de sessionStorage si no está en context
    try {
      const businessData = sessionStorage.getItem('businessData');
      if (businessData) {
        const parsedData = JSON.parse(businessData);
        if (parsedData.logo_url) {
          return parsedData.logo_url;
        }
      }
    } catch (e) {
      console.error("Error parsing business data for logo:", e);
    }
    
    // Logo por defecto si no hay logo del negocio
    return "/logo-horizontal.png";
  };

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const user = await getCurrentUser();
        const attributes = await fetchUserAttributes();
        const firstName = attributes["custom:firstName"] || user.username;
        setUser({ ...user, firstName });
      } catch (error) {
        console.error("Error fetching user:", error);
      }
    };
    
    if (isAuthenticated) {
      fetchUser();
    }
  }, [isAuthenticated]);

  // Handle business change
  const handleBusinessChange = (businessId: string) => {
    if (businessId !== currentBusinessId) {
      // Guardar el ID del negocio en sessionStorage antes de cambiar
      sessionStorage.setItem('selectedBusinessId', businessId);
      
      // Lógica para actualizar el negocio
      switchBusiness(businessId)
        .then(() => {
          // Recargar la página para aplicar todos los cambios
          window.location.reload();
        })
        .catch(error => {
          console.error("Error switching business:", error);
        });
    }
  };

  // Create menu items with highlights for current business
  const getBusinessMenuItems = () => {
    const items = availableBusinesses.map(b => ({
      label: b.name,
      id: b.id,
      // Add a visual indicator for the current business
      selected: b.id === currentBusinessId
    }));
    return items;
  };

  return (
    <div className="hdr">
      <div className="hdr-left">
        {isAuthenticated && onMenuClick && (
          <button
            onClick={onMenuClick}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '4px'
            }}
            className="mobile-menu-btn"
            aria-label={t("header.openMenu")}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="12" x2="21" y2="12"></line>
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <line x1="3" y1="18" x2="21" y2="18"></line>
            </svg>
          </button>
        )}
        {/* SAM-944: the business mark shows on a phone only (global.css). */}
        <span className="hdr-logo" aria-hidden>{initialsOf(headerTitle)}</span>
        <div className="hdr-biz">
          {headerTitle}
        </div>
      </div>
      
      {isAuthenticated && (
        <div className="hdr-right">
          {hasMultipleBusinesses && (
            <Popover
              content={({ close }) => (
                <StatefulMenu
                  items={getBusinessMenuItems()}
                  onItemSelect={({ item }) => {
                    handleBusinessChange(item.id as string);
                    close();
                  }}
                />
              )}
              placement="bottom"
              overrides={POPOVER_OVERRIDES}
            >
              <div className="tenant-switch" style={{ cursor: "pointer" }}>
                {business?.name || t("header.selectBusiness")}
                <ChevronDown size={14} />
              </div>
            </Popover>
          )}

          <div className="ico" style={{ marginRight: '8px' }}>
            <Bell size={20} />
          </div>

          <StatefulPopover
            content={({ close }) => (
              <StatefulMenu
                items={menuItems}
                onItemSelect={({ item }) => {
                  if (item.id === "logout" && onLogOut) {
                    onLogOut();
                  } else if (typeof item.id === "string" && item.id.startsWith("lang:")) {
                    setLang(item.id.slice(5) as Lang);
                  }
                  close();
                }}
              />
            )}
            placement="bottomRight"
            overrides={POPOVER_OVERRIDES}
          >
            <div className="acct">
              <div className="avatar" style={{ width: '32px', height: '32px' }}>
                {user?.firstName ? user.firstName.charAt(0).toUpperCase() : t("header.userInitial")}
              </div>
              <span className="acct-name">{user?.firstName || t("header.user")}</span>
              <ChevronDown size={14} className="acct-chev" />
            </div>
          </StatefulPopover>
        </div>
      )}
    </div>
  );
};
